import { Alert, Table } from "react-bootstrap";
import { CommonAPIRequestSummary } from "../types";
import Link from "next/link";
import {
  FilterBadge,
  FilterCriteria,
  StatusBadge,
} from "@bagel-web/components";
import { formatTimestamp } from "../util";

export enum APIRequestTableColumn {
  CREATED,
  ORGANIZATION,
  PROJECT,
  API_KEY,
  METHOD,
  PATH,
  STATUS_CODE,
  AGENT_ID,
  SESSION_ID,
}

const getColumnStyle = (column: APIRequestTableColumn) => {
  switch (column) {
    case APIRequestTableColumn.API_KEY:
      return { minWidth: "150px" };
    case APIRequestTableColumn.CREATED:
      return { minWidth: "150px", whiteSpace: "nowrap" as const };
    case APIRequestTableColumn.PATH:
      return { minWidth: "200px" };
    default:
      return {};
  }
};

const APIRequestRow = ({
  apiRequest,
  columns,
  getAPIRequestLink,
  pathMasks,
  onSetFilters,
}: {
  apiRequest: CommonAPIRequestSummary;
  columns: Array<APIRequestTableColumn>;
  getAPIRequestLink: (apiRequest: CommonAPIRequestSummary) => string;
  pathMasks?: Array<string>;
  onSetFilters: (criteria: FilterCriteria<string>) => void;
}) => {
  let maskedPath = apiRequest.req_path;
  if (pathMasks) {
    pathMasks.forEach(
      (pathMask) => (maskedPath = maskedPath.replaceAll(pathMask, "..."))
    );
  }

  return (
    <tr>
      {columns.map((column) => (
        <td key={column} style={getColumnStyle(column)}>
          {column === APIRequestTableColumn.CREATED &&
            formatTimestamp(apiRequest.created)}
          {column === APIRequestTableColumn.ORGANIZATION &&
            apiRequest.organization}
          {column === APIRequestTableColumn.PROJECT && apiRequest.project_name}
          {column === APIRequestTableColumn.API_KEY &&
            (apiRequest.api_key_id ? (
              <FilterBadge
                onClick={() =>
                  apiRequest.api_key_id &&
                  onSetFilters({ apiKeyId: { value: apiRequest.api_key_id } })
                }
                content={apiRequest.api_key_name}
              />
            ) : (
              apiRequest.api_key_name
            ))}
          {column === APIRequestTableColumn.METHOD && (
            <code>{apiRequest.req_method}</code>
          )}
          {column === APIRequestTableColumn.PATH && (
            <Link href={getAPIRequestLink(apiRequest)}>
              <code style={{ wordBreak: "break-all" }}>{maskedPath}</code>
            </Link>
          )}
          {column === APIRequestTableColumn.STATUS_CODE && (
            <StatusBadge
              statusCode={apiRequest.res_status_code}
              created={apiRequest.created}
            />
          )}
          {column === APIRequestTableColumn.AGENT_ID && (
            <code>{apiRequest.agent_id}</code>
          )}
          {column === APIRequestTableColumn.SESSION_ID && (
            <code>{apiRequest.session_id}</code>
          )}
        </td>
      ))}
    </tr>
  );
};

const getColumnLabel = (column: APIRequestTableColumn): string => {
  switch (column) {
    case APIRequestTableColumn.CREATED:
      return "Time";
    case APIRequestTableColumn.ORGANIZATION:
      return "Organization";
    case APIRequestTableColumn.API_KEY:
      return "API Key";
    case APIRequestTableColumn.PROJECT:
      return "Project";
    case APIRequestTableColumn.METHOD:
      return "Method";
    case APIRequestTableColumn.PATH:
      return "Path";
    case APIRequestTableColumn.STATUS_CODE:
      return "Response";
    case APIRequestTableColumn.AGENT_ID:
      return "Agent ID";
    case APIRequestTableColumn.SESSION_ID:
      return "Session ID";
    default:
      return "";
  }
};

const APIRequestsTable = ({
  apiRequests,
  columns,
  getAPIRequestLink,
  pathMasks,
  onSetFilters,
}: {
  apiRequests: Array<CommonAPIRequestSummary>;
  columns: Array<APIRequestTableColumn>;
  getAPIRequestLink: (apiRequest: CommonAPIRequestSummary) => string;
  pathMasks?: Array<string>;
  onSetFilters: (criteria: FilterCriteria<string>) => void;
}) => {
  if (apiRequests.length === 0) {
    return <Alert className="mt-3">No matching API requests.</Alert>;
  }

  return (
    <Table data-testid="api-requests-table">
      <thead>
        <tr>
          {columns.map((column) => (
            <th key={column}>{getColumnLabel(column)}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {apiRequests.map((r) => {
          return (
            <APIRequestRow
              key={r.id}
              apiRequest={r}
              columns={columns}
              getAPIRequestLink={getAPIRequestLink}
              pathMasks={pathMasks}
              onSetFilters={onSetFilters}
            />
          );
        })}
      </tbody>
    </Table>
  );
};

export default APIRequestsTable;
